import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { Target, Eye, MapPin, BadgeCheck, Wrench, Award, ArrowRight } from "lucide-react";

const values = [
  { icon: Wrench, title: "System Integration Expertise", desc: "End-to-end design, supply, installation and commissioning of ELV, security and AV systems — one accountable partner." },
  { icon: BadgeCheck, title: "Compliance & Quality", desc: "Solutions aligned with Malaysian regulatory requirements and industry best practices, using certified hardware." },
  { icon: Award, title: "Certified Engineering", desc: "Trained technical team experienced with leading OEM platforms for CCTV, access control, PA and structured cabling." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        label="About YC System"
        title="Security & AV Integrator You Can Trust"
        subtitle="YC SYSTEM is a Malaysian company specialising in the trading and integration of ELV, CCTV and security systems for commercial, industrial and residential clients."
        image="https://images.unsplash.com/photo-1596422846543-75c6fc197f07?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="tech-label">Who We Are</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white mt-2">
              Based in the Heart of Kuala Lumpur
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Operating from Menara Keck Seng on Jalan Bukit Bintang, YC SYSTEM serves clients
              across the Klang Valley and Malaysia. We combine quality hardware supply with
              professional system design and integration, delivering solutions that are
              reliable, scalable and easy to manage.
            </p>
            <p className="mt-4 text-slate-300 leading-relaxed">
              From a single retail outlet to an entire commercial tower, we handle every layer
              of low-voltage infrastructure: surveillance, access control, alarms, intercom,
              structured cabling, public address and audio-visual systems.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="spec-chip">REG. NO. 201903139239</span>
              <span className="spec-chip">EST. 2019</span>
              <span className="spec-chip">MALAYSIA</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="glow-card overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1596422846543-75c6fc197f07?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
              alt="Kuala Lumpur skyline"
              className="w-full h-64 object-cover"
            />
            <div className="p-6">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-cyan-400 shrink-0 mt-1" />
                <div>
                  <div className="font-display text-white font-semibold">Headquarters</div>
                  <p className="text-sm text-slate-400 mt-1">
                    Suite 33-01, 33rd Floor, Menara Keck Seng, 203 Jalan Bukit Bintang,
                    55100 Kuala Lumpur, Malaysia
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-[#0F172A]/60 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glow-card p-8" data-testid="mission-card">
              <Target className="w-8 h-8 text-cyan-400 mb-4" />
              <h3 className="font-display text-xl font-semibold text-white">Our Mission</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                To protect people, property and operations by delivering dependable security,
                ELV and AV systems — engineered precisely, installed professionally and
                supported for the long term.
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="glow-card p-8" data-testid="vision-card">
              <Eye className="w-8 h-8 text-cyan-400 mb-4" />
              <h3 className="font-display text-xl font-semibold text-white">Our Vision</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                To be Malaysia's most trusted low-voltage systems integrator — the first name
                businesses call when safety, connectivity and communication matter.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glow-card p-8"
                data-testid={`value-${v.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <v.icon className="w-8 h-8 text-cyan-400 mb-4" />
                <h3 className="font-display text-lg font-semibold text-white">{v.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/quote"
              data-testid="about-quote-cta"
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-colors border border-cyan-400/40"
            >
              Discuss Your Project <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
